import { useCallback, useEffect, useRef, useState, type ChangeEvent, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { PencilSimpleIcon } from '@phosphor-icons/react/dist/csr/PencilSimple';
import { TrashIcon } from '@phosphor-icons/react/dist/csr/Trash';
import { XIcon } from '@phosphor-icons/react/dist/csr/X';
import { CaretDownIcon } from '@phosphor-icons/react/dist/csr/CaretDown';
import { CaretUpIcon } from '@phosphor-icons/react/dist/csr/CaretUp';
import { PlusIcon } from '@phosphor-icons/react/dist/csr/Plus';

import {
  CATEGORY_FILTER_LABEL_KEY,
  CATEGORY_LABEL_KEY,
  PUBLISHED_FILTER_LABEL_KEY,
  type ArticleCategoryFilter,
  type PublishedFilter,
} from './articleLabels';
import { ArticleFormDialog } from './ArticleFormDialog';
import { PageActions } from '../../app/shell/PageActions';
import { FilterBar } from '../../design-system/composites/FilterBar/FilterBar';
import { AttributeTag } from '../../design-system/primitives/AttributeTag/AttributeTag';
import { Button } from '../../design-system/primitives/Button/Button';
import { Dialog } from '../../design-system/composites/Dialog/Dialog';
import { EmptyState } from '../../design-system/composites/DataTable/states/EmptyState';
import { ErrorState } from '../../design-system/composites/DataTable/states/ErrorState';
import { NoResultsState } from '../../design-system/composites/DataTable/states/NoResultsState';
import { Pagination } from '../../design-system/composites/Pagination/Pagination';
import { useCursorPages } from '../../design-system/composites/Pagination/useCursorPages';
import { Input } from '../../design-system/primitives/Input/Input';
import { SegmentedControl } from '../../design-system/primitives/SegmentedControl/SegmentedControl';
import { ApiError } from '../../lib/api/client';
import {
  knowledgeApi,
  type ArticleCategory,
  type KnowledgeArticle,
  type KnowledgeArticleQuery,
} from '../../lib/api/knowledge';
import { ExportButton } from '../shared/ExportButton';
import { canManageKnowledge, canReadKnowledge } from '../../lib/permissions/knowledge';
import styles from './ArticleList.module.css';

/**
 * The knowledge-base FAQ (M-12, Task 4, spec §3, K-6, K-7) — `/knowledge-base`,
 * reachable for a session holding `knowledge.view`
 * (`src/lib/permissions/knowledge.ts`'s own `canReadKnowledge`,
 * `App.tsx`'s `rendersKnowledgeBase`, `navigation.ts`'s pre-existing
 * `knowledgeBase` entry). Copies `RequestList.tsx`'s structure (draft/
 * applied filters, a cursor-paginated fetch, an error-recovery block with a
 * support reference) — the task brief's own Step 1.
 *
 * ## NOT a `DataTable` (task brief Step 3, spec §3)
 *
 * A table cell holding a nineteen-item pharmacy list is why the legacy
 * screen is unreadable (spec §0). Each entry renders as an expandable row —
 * the question always visible, the answer revealed on click — never a
 * `<td>`. `EmptyState`/`ErrorState`/`NoResultsState` are reused directly
 * from `DataTable`'s own `states/` folder: all three are already plain
 * `<div>`s with no table coupling (`ListState.tsx`'s own class docblock),
 * so reusing them here is not a second visual language, only
 * `LoadingSkeleton` — which draws real `<tr>`s at the table row height — is
 * left behind, replaced by a handful of plain placeholder rows below.
 *
 * ## K-6: `{article.answer}` is a React TEXT CHILD, never
 * `dangerouslySetInnerHTML` — THIS IS THE ONLY XSS DEFENCE THIS MODULE HAS
 *
 * Spec §0: `question`/`answer` are stored and echoed completely unescaped in
 * the legacy, which is a stored XSS reachable by every call-centre agent,
 * supervisor and administrator. The API reproduces that storage
 * byte-for-byte on purpose (`KnowledgeBaseArticleController`'s own class
 * docblock: "no sanitisation... on the way in or the way out") — spec K-6's
 * decision is that the DEFENCE lives here, at the render site, and nowhere
 * else: `renderRow()` below puts `article.question` and `article.answer`
 * into JSX as plain `{...}` expressions, which React escapes by
 * construction. `dangerouslySetInnerHTML`, a markdown renderer, or a link
 * autolinker anywhere in this module would reopen exactly the hole spec §0
 * documents. See `ArticleList.test.tsx`'s own XSS suite for the kill.
 *
 * ## The manage affordances never exist without `knowledge.manage` (task
 * brief Step 4)
 *
 * The Draft/Published segmented control, the draft `AttributeTag`, the
 * "New article" button and each row's Edit/Delete controls are ALL gated on
 * `canManageKnowledge(permissions)` — a single shared function
 * (`src/lib/permissions/knowledge.ts`), never copied inline. Row-level
 * Edit/Delete additionally read `article.can.manage`
 * (`KnowledgeBaseArticleResource`'s own published ability), never
 * re-derived from `permissions` alone — the Global Constraint
 * `JustificationDetail.tsx`'s `record.can.answer`/`record.can.decide`
 * discipline states, restated here because a `knowledge.manage` holder and
 * "this row is editable" happen to be the same fact for every row today
 * (this policy has no row-level condition — `KnowledgeBaseArticlePolicy`'s
 * own class docblock), which makes it easy to forget the shape is meant to
 * survive a POLICY that later grows one.
 *
 * `published` IS NEVER PUT ON THE OUTGOING QUERY AT ALL for a caller without
 * `knowledge.manage` — see `toQuery()` below. The server would silently
 * ignore the filter for such a caller anyway (spec §2), but this client
 * does not rely on that: the key is simply absent.
 */

export interface ArticleListProps {
  permissions: readonly string[];
}

type ListStatus = 'loading' | 'ready' | 'empty' | 'no-results' | 'error';

interface FilterState {
  category: ArticleCategoryFilter;
  published: PublishedFilter;
}

const BLANK_FILTERS: FilterState = { category: 'all', published: 'all' };

/**
 * `canManage` decides whether `published` is even a candidate for the
 * outgoing query — never merely whether its VALUE is `'all'`. A caller who
 * lost `knowledge.manage` mid-session (a permission change reflected on the
 * next sign-in) must never have a stale `published` filter reach the wire.
 */
function toQuery(
  filters: FilterState,
  q: string,
  canManage: boolean,
  cursor?: string,
  perPage?: number,
): KnowledgeArticleQuery {
  const query: KnowledgeArticleQuery = {};
  if (filters.category !== 'all') query.category = filters.category as ArticleCategory;
  if (q.trim() !== '') query.q = q.trim();
  if (canManage && filters.published !== 'all') {
    query.published = filters.published === 'published';
  }
  if (cursor !== undefined) query.cursor = cursor;
  if (perPage !== undefined) query.per_page = perPage;
  return query;
}

/** Read off the OUTGOING QUERY, never `appliedFilters` state — `RequestList.tsx`'s own `hasAppliedFilters` shape. */
function hasAppliedFilters(query: KnowledgeArticleQuery): boolean {
  return query.category !== undefined || query.q !== undefined || query.published !== undefined;
}

export function ArticleList({ permissions }: ArticleListProps) {
  const { t } = useTranslation();
  const canManage = canManageKnowledge(permissions);

  const [filters, setFilters] = useState<FilterState>(BLANK_FILTERS);
  const [draftQ, setDraftQ] = useState('');
  const [appliedQ, setAppliedQ] = useState('');
  const [status, setStatus] = useState<ListStatus>('loading');
  const [rows, setRows] = useState<readonly KnowledgeArticle[]>([]);
  const [reference, setReference] = useState<string | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  // 50 — `ArticleIndexRequest::DEFAULT_PER_PAGE` server-side.
  const pages = useCursorPages();
  // Destructured — `pages` is a fresh object every render; see
  // `CaseList.tsx`'s identical note.
  const { observe } = pages;
  const [expanded, setExpanded] = useState<ReadonlySet<string>>(new Set());

  const [formOpen, setFormOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState<KnowledgeArticle | undefined>(undefined);

  const [pendingDelete, setPendingDelete] = useState<KnowledgeArticle | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteFailure, setDeleteFailure] = useState<string | null>(null);
  const [deleteReference, setDeleteReference] = useState<string | null>(null);

  const requestSeq = useRef(0);

  const load = useCallback(async (query: KnowledgeArticleQuery) => {
    const seq = ++requestSeq.current;
    setStatus('loading');
    setReference(null);

    try {
      const page = await knowledgeApi.list(query);
      if (seq !== requestSeq.current) return;
      setRows(page.data);
      setTotal(page.meta.total);
      observe({ next_cursor: page.meta.next_cursor, total: page.meta.total });
      setStatus(page.data.length === 0 ? (hasAppliedFilters(query) ? 'no-results' : 'empty') : 'ready');
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      if (seq !== requestSeq.current) return;
      setReference(error.requestId ?? null);
      setStatus('error');
    }
  }, [observe]);

  useEffect(() => {
    void load(toQuery(filters, appliedQ, canManage, pages.cursor ?? undefined, pages.perPage));
  }, [load, filters, appliedQ, canManage, pages.cursor, pages.perPage]);

  function handleCategoryChange(value: string): void {
    setFilters((current) => ({ ...current, category: value as ArticleCategoryFilter }));
    pages.first();
  }

  function handlePublishedChange(value: string): void {
    setFilters((current) => ({ ...current, published: value as PublishedFilter }));
    pages.first();
  }

  function handleFilterSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    pages.first();
    setAppliedQ(draftQ);
  }

  function handleClearFilters(): void {
    setFilters(BLANK_FILTERS);
    setDraftQ('');
    setAppliedQ('');
    pages.first();
  }

  function retry(): void {
    void load(toQuery(filters, appliedQ, canManage, pages.cursor ?? undefined, pages.perPage));
  }

  function toggleExpanded(uuid: string): void {
    setExpanded((current) => {
      const next = new Set(current);
      if (next.has(uuid)) {
        next.delete(uuid);
      } else {
        next.add(uuid);
      }
      return next;
    });
  }

  /** Whether a freshly created or edited row still belongs on THIS page under the CURRENT filters — never assumed. */
  function matchesCurrentFilters(article: KnowledgeArticle): boolean {
    if (filters.category !== 'all' && article.category !== filters.category) return false;
    if (canManage && filters.published !== 'all') {
      const wantPublished = filters.published === 'published';
      if (article.is_published !== wantPublished) return false;
    }
    return true;
  }

  function openCreate(): void {
    setEditingArticle(undefined);
    setFormOpen(true);
  }

  function openEdit(article: KnowledgeArticle): void {
    setEditingArticle(article);
    setFormOpen(true);
  }

  function closeForm(): void {
    setFormOpen(false);
    setEditingArticle(undefined);
  }

  /**
   * A freshly created entry is ALWAYS a draft (spec K-2) — never prepended
   * into a view filtered to `published`, and never while a search term is
   * applied: this list has no client-side way to know whether the term
   * matches the new row's `question`/`answer` (spec K-5's substring match is
   * server-side only).
   */
  function handleCreated(created: KnowledgeArticle): void {
    if (matchesCurrentFilters(created) && appliedQ.trim() === '') {
      setRows((current) => [created, ...current]);
      // Promotes out of EITHER `'empty'` or `'no-results'` — the row just
      // landed on screen either way, so a stale "no matches"/"no articles
      // yet" message must never survive it (I-1).
      setStatus('ready');
    }
    closeForm();
  }

  /** An edit that moves a row out of the current filters (e.g. publishing it while "Draft" is selected) removes it, rather than showing a row the current query would not have returned. */
  function handleUpdated(saved: KnowledgeArticle): void {
    setRows((current) => {
      const next = matchesCurrentFilters(saved)
        ? current.map((row) => (row.uuid === saved.uuid ? saved : row))
        : current.filter((row) => row.uuid !== saved.uuid);
      // Same fix as `confirmDelete`'s own comment below: dropping the last
      // visible row must re-derive `status`, never leave it at its stale
      // 'ready' value — otherwise an empty `<ul>` renders with no Empty/
      // No-results message (I-2).
      if (next.length === 0) {
        setStatus(
          // `cursor`/`per_page` never affect `hasAppliedFilters`; omitted.
          hasAppliedFilters(toQuery(filters, appliedQ, canManage)) ? 'no-results' : 'empty',
        );
      }
      return next;
    });
    closeForm();
  }

  function handleSaved(saved: KnowledgeArticle): void {
    if (editingArticle === undefined) {
      handleCreated(saved);
    } else {
      handleUpdated(saved);
    }
  }

  function openDeleteDialog(article: KnowledgeArticle): void {
    setPendingDelete(article);
    setDeleteFailure(null);
    setDeleteReference(null);
  }

  function closeDeleteDialog(): void {
    if (deleting) return;
    setPendingDelete(null);
    setDeleteFailure(null);
    setDeleteReference(null);
  }

  async function confirmDelete(): Promise<void> {
    if (pendingDelete === null || deleting) return;
    const uuid = pendingDelete.uuid;
    setDeleting(true);
    setDeleteFailure(null);
    setDeleteReference(null);

    try {
      await knowledgeApi.remove(uuid);
      setRows((current) => {
        const next = current.filter((row) => row.uuid !== uuid);
        // Deleting the last visible row leaves `status` at its previous
        // 'ready' value otherwise — an empty `<ul>` with no message, rather
        // than the Empty/No-results state a fresh load of the same query
        // would show.
        if (next.length === 0) {
          setStatus(
            // `cursor`/`per_page` never affect `hasAppliedFilters`; omitted.
          hasAppliedFilters(toQuery(filters, appliedQ, canManage)) ? 'no-results' : 'empty',
          );
        }
        return next;
      });
      setPendingDelete(null);
    } catch (error) {
      if (!(error instanceof ApiError)) throw error;
      // NOT closeDeleteDialog(): kept open so the failure's own sentence
      // stays readable — `CodeList.tsx`'s own choice for the identical shape.
      setDeleteFailure(t('knowledge.deleteFailed'));
      setDeleteReference(error.requestId ?? null);
    } finally {
      setDeleting(false);
    }
  }

  const categoryOptions = (['all', 'sensor', 'replacement', 'defect', 'other'] as const).map(
    (value) => ({ value, label: t(CATEGORY_FILTER_LABEL_KEY[value]) }),
  );
  const publishedOptions = (['all', 'published', 'draft'] as const).map((value) => ({
    value,
    label: t(PUBLISHED_FILTER_LABEL_KEY[value]),
  }));

  const announcement = (): string => {
    switch (status) {
      case 'loading':
        return t('list.loading');
      case 'empty':
        return t('knowledge.emptyTitle');
      case 'no-results':
        return t('list.noResultsTitle');
      case 'error':
        return '';
      default:
        return t('table.rowCount', { count: rows.length });
    }
  };

  return (
    <div className={styles['page']}>
      {/* No second heading under the shell's `<h1>`: the page's commands
          sit in the title row instead. */}
      <PageActions>
        {/* `canManage` is passed through because `toQuery` uses it to keep
            `published` off the wire for a caller without `knowledge.manage` —
            the export must not filter by something the caller's own list
            cannot. `appliedQ`, not the draft search box. No cursor. */}
        <ExportButton
          module="knowledge-base"
          filters={{ ...toQuery(filters, appliedQ, canManage) }}
          canExport={canReadKnowledge(permissions)}
        />
        {canManage ? (
          <Button onClick={openCreate} icon={<PlusIcon size={16} />}>
            {t('knowledge.newArticle')}
          </Button>
        ) : null}
      </PageActions>

      <FilterBar
        onSubmit={handleFilterSubmit}
        onClear={handleClearFilters}
        leading={
          <div className={styles['segments']}>
            <SegmentedControl
              label={t('knowledge.categoryFilterLabel')}
              name="knowledgeCategory"
              options={categoryOptions}
              value={filters.category}
              onChange={handleCategoryChange}
            />

            {/* Draft/Published filter — ONLY for a `knowledge.manage` holder
                (task brief Step 4): without the permission this control does
                not exist, and `toQuery()` never puts `published` on the wire
                regardless of what `filters.published` holds. */}
            {canManage ? (
              <SegmentedControl
                label={t('knowledge.publishedFilterLabel')}
                name="knowledgePublished"
                options={publishedOptions}
                value={filters.published}
                onChange={handlePublishedChange}
              />
            ) : null}
          </div>
        }
      >
        <Input
          label={t('knowledge.searchLabel')}
          width="fill"
          dir="auto"
          value={draftQ}
          onChange={(event: ChangeEvent<HTMLInputElement>) => {
            setDraftQ(event.target.value);
          }}
        />
      </FilterBar>

      <div className={styles['srOnly']} role="status" aria-live="polite">
        {announcement()}
      </div>

      {status === 'loading' ? (
        <ul className={styles['list']} aria-busy="true">
          {Array.from({ length: 4 }, (_, index) => (
            <li key={index} className={styles['row']}>
              <div className={styles['rowHeader']}>
                <span className={styles['srOnly']}>{t('list.loading')}</span>
              </div>
            </li>
          ))}
        </ul>
      ) : null}

      {status === 'ready' ? (
        <ul className={styles['list']}>
          {rows.map((article) => {
            const isExpanded = expanded.has(article.uuid);
            const panelId = `knowledge-answer-${article.uuid}`;

            return (
              <li key={article.uuid} className={styles['row']}>
                <div className={styles['rowHeader']}>
                  <button
                    type="button"
                    className={styles['toggle']}
                    dir="auto"
                    aria-expanded={isExpanded}
                    aria-controls={panelId}
                    onClick={() => {
                      toggleExpanded(article.uuid);
                    }}
                  >
                    <span className={styles['caret']} aria-hidden="true">
                      {isExpanded ? <CaretUpIcon size={14} /> : <CaretDownIcon size={14} />}
                    </span>
                    {/* `{article.question}` — a React text child, never HTML
                        (K-6). See this file's own class docblock. */}
                    {article.question}
                  </button>

                  <div className={styles['rowTags']}>
                    <AttributeTag>{t(CATEGORY_LABEL_KEY[article.category])}</AttributeTag>
                    {canManage && !article.is_published ? (
                      <AttributeTag tone="outline">{t('knowledge.draftBadge')}</AttributeTag>
                    ) : null}
                  </div>

                  {article.can.manage ? (
                    <div className={styles['rowActions']}>
                      <Button
                        icon={<PencilSimpleIcon size={16} />}
                        variant="secondary"
                        onClick={() => {
                          openEdit(article);
                        }}
                      >
                        {t('knowledge.edit')}
                      </Button>
                      <Button
                        icon={<TrashIcon size={16} />}
                        variant="ghost"
                        onClick={() => {
                          openDeleteDialog(article);
                        }}
                      >
                        {t('knowledge.delete')}
                      </Button>
                    </div>
                  ) : null}
                </div>

                {isExpanded ? (
                  // K-6: `article.answer` renders as a React text child in an
                  // element with `white-space: pre-wrap` — NEVER
                  // `dangerouslySetInnerHTML`, no markdown renderer, no link
                  // autolinker. See this file's own class docblock.
                  <p id={panelId} className={styles['answer']} dir="auto">
                    {article.answer}
                  </p>
                ) : null}
              </li>
            );
          })}
        </ul>
      ) : null}

      {status === 'empty' ? (
        <EmptyState title={t('knowledge.emptyTitle')} body={t('knowledge.emptyBody')} />
      ) : null}

      {status === 'no-results' ? (
        <NoResultsState title={t('list.noResultsTitle')} onClearFilters={handleClearFilters} />
      ) : null}

      {status === 'error' ? (
        <>
          <ErrorState title={t('knowledge.queueLoadFailed')} onRetry={retry} />
          <div className={styles['recovery']}>
            {reference === null ? null : (
              <p className={styles['reference']}>
                {t('auth.supportReference')}{' '}
                <bdi dir="ltr" data-testid="knowledgeListRequestId">
                  {reference}
                </bdi>
              </p>
            )}
          </div>
        </>
      ) : null}

      <Pagination pages={pages} total={total} rowsOnPage={rows.length} />

      <ArticleFormDialog open={formOpen} article={editingArticle} onClose={closeForm} onSaved={handleSaved} />

      <Dialog
        open={pendingDelete !== null}
        destructive
        title={t('knowledge.confirmDeleteTitle')}
        onClose={closeDeleteDialog}
        actions={
          <>
            <Button
              icon={<TrashIcon size={16} />}
              variant="destructive"
              loading={deleting}
              onClick={() => {
                void confirmDelete();
              }}
            >
              {t('knowledge.confirm')}
            </Button>
            <Button icon={<XIcon size={16} />} variant="ghost" onClick={closeDeleteDialog}>
              {t('knowledge.cancel')}
            </Button>
          </>
        }
      >
        <p className={styles['deleteBody']}>{t('knowledge.confirmDeleteBody')}</p>

        <div className={styles['deleteFailure']} role="alert" aria-atomic="true">
          {deleteFailure}
        </div>

        {deleteReference === null ? null : (
          <p className={styles['reference']}>
            {t('auth.supportReference')}{' '}
            <bdi dir="ltr" data-testid="knowledgeDeleteRequestId">
              {deleteReference}
            </bdi>
          </p>
        )}
      </Dialog>
    </div>
  );
}
